import {makeAutoObservable} from "mobx";
import Accordion from "../models/Accordion";
import agent from "../api/agent";
import {Brand} from "../models/Brand";
import {BassType} from "../models/BassType";
import {RightHandSystem} from "../models/RightHandSystem";
import {getDistinct, getMinMax} from "../assets/utils/arrayHelpers";

export default class AccordionStore {
    mode: 'rent' | 'buy' = 'buy';
    isLoading: boolean = false;
    accordions: Accordion[] = [];
    filteredAccordions: Accordion[] = [];
    filteredBrand: Brand | undefined;
    filteredBassType: BassType | undefined;
    filteredRightSystem: RightHandSystem | undefined;
    filteredRightBtnRow: number | undefined;
    filteredNotesRightBtn: number | undefined;
    filteredChorusRight: number | undefined;
    filteredCassottoRight: number | undefined;
    filteredRegisterRight: number | undefined;
    filteredChinSwitches: number | undefined;
    filteredRowsBtnLeft: number | undefined;
    filteredStandardBass: number | undefined;
    filteredRegisterSb: string | undefined;
    filteredNotesFb: number | undefined;
    filteredRegisterFb: string | undefined;
    filteredChorusFb: string | undefined;
    filteredNotesRightPiano: number | undefined;
    filteredStandardBassChorus: number | undefined;
    filteredTamReeds: boolean = false;
    filteredFamReeds: boolean = false;
    filteredDurallReeds: boolean = false;
    filteredTurboReeds: boolean = false;
    filteredUsed: boolean = true;
    filteredWeight: [number, number] = [0, 20];
    filteredHeight: [number, number] = [0, 100];
    filteredWidth: [number, number] = [0, 100];
    attributeIsLoading: Map<string, boolean> = new Map<string, boolean>();

    constructor(){
        makeAutoObservable(this)
        this.findAll().then(() => {
            this.setFilteredWeight(this.findLightiestAndHeaviestWeight())
            this.setFilteredHeight(this.findLowestAndHighestHeight())
            this.setFilteredWidth(this.findLowestAndHighestWidth())
            if(this.accordions.length > 0) {
                Array.from(Object.keys(this.accordions[0])).forEach(k => {
                    this.attributeIsLoading.set(k, false);
                })
            }
        });
    }

    async findAll(){
        this.setIsLoading(true);
        const acc: Accordion[] = await agent.Accordions.findAll();
        if(!acc) return;
        this.setAccordions([...acc])
        if(this.mode === 'rent') this.setFilteredAccordions(acc.filter(a => a.rental));
        else this.setFilteredAccordions(acc);
        this.setIsLoading(false);
    }

    update = async (accordion: Accordion, attributeName?: string) => {
        if(attributeName) this.setAttributeIsLoading(attributeName, true);
        else this.setIsLoading(true)
        accordion.updatedAt = new Date(Date.now())
        const newAccordion = await agent.Accordions.update(accordion);
        if(attributeName) this.setAttributeIsLoading(attributeName, false);
        else this.setIsLoading(false)
        if(!attributeName) this.setAccordions(this.accordions.map(a => a.id === newAccordion.id ? newAccordion : a));
        else {
            //@ts-ignore
            this.setAccordions(this.accordions.map(a => a.id === newAccordion.id ? {...a, [attributeName]: newAccordion[attributeName]} : a))
        }
    }

    create = async () => {
        this.setIsLoading(true);
        const newAccordion = await agent.Accordions.create();
        this.setAccordions([newAccordion, ...this.accordions])
        this.resetFilter();
        this.setIsLoading(false);
    }

    delete = async (id: number) => {
        this.setIsLoading(true);
        const returnedId = await agent.Accordions.del(id);
        if(returnedId){
            this.setAccordions(this.accordions.filter(a => a.id !== returnedId));
            this.setIsLoading(false);
        }
    }

    setAsThumbnail = async (accordionId: number, imageId: number) => {
        const updatedAccordion = await agent.Accordions.setAsThumbnail(accordionId, imageId)
        this.setAccordions(this.accordions.map(a => a.id === accordionId ? updatedAccordion : a));
    }

    applyFilter = () => {
        let accordions = this.accordions;
        if(this.filteredBrand) accordions = accordions.filter(a => a.brand === this.filteredBrand);
        if(this.filteredBassType) accordions = accordions.filter(a => a.bassType === this.filteredBassType);
        if(this.filteredRightSystem) accordions = accordions.filter(a => a.keysOrBut === this.filteredRightSystem);
        if(this.filteredRightBtnRow) accordions = accordions.filter(a => a.rowsButRight === this.filteredRightBtnRow);
        if(this.filteredNotesRightBtn) accordions = accordions.filter(a => a.buttonRight === this.filteredNotesRightBtn && a.keysOrBut === RightHandSystem.Button);
        if(this.filteredNotesRightPiano) accordions = accordions.filter(a => a.keysPianoRight === this.filteredNotesRightPiano && a.keysOrBut === RightHandSystem.Piano);
        if(this.filteredChorusRight) accordions = accordions.filter(a => a.chorusRight === this.filteredChorusRight);
        if(this.filteredCassottoRight) accordions = accordions.filter(a => a.casRight === this.filteredCassottoRight);
        if(this.filteredRegisterRight) accordions = accordions.filter(a => a.registerRight === this.filteredRegisterRight);
        if(this.filteredChinSwitches) accordions = accordions.filter(a => a.chinSwitches === this.filteredChinSwitches);
        if(this.filteredRowsBtnLeft) accordions = accordions.filter(a => a.rowsButLeft === this.filteredRowsBtnLeft);
        if(this.filteredStandardBass) accordions = accordions.filter(a => a.sbLeft === this.filteredStandardBass);
        if(this.filteredStandardBassChorus) accordions = accordions.filter(a => a.chorusSb === this.filteredStandardBassChorus);
        if(this.filteredRegisterSb) accordions = accordions.filter(a => a.registerSbLeft === this.filteredRegisterSb);
        if(this.filteredNotesFb) accordions = accordions.filter(a => a.fbLeft === this.filteredNotesFb);
        if(this.filteredRegisterFb) accordions = accordions.filter(a => a.registerFbLeft === this.filteredRegisterFb);
        if(this.filteredChorusFb) accordions = accordions.filter(a => a.chorusFb === this.filteredChorusFb);
        if(this.filteredTamReeds) accordions = accordions.filter(a => a.tam);
        if(this.filteredFamReeds) accordions = accordions.filter(a => a.fam);
        if(this.filteredTurboReeds) accordions = accordions.filter(a => a.turbo);
        if(this.filteredDurallReeds) accordions = accordions.filter(a => a.durall);
        if(this.filteredUsed) accordions = accordions.filter(a => a.used);
        else accordions = accordions.filter(a => !a.used);
        accordions = accordions.filter(a => a.weight >= this.filteredWeight[0] && a.weight <= this.filteredWeight[1]);
        accordions = accordions.filter(a => a.height >= this.filteredHeight[0] && a.height <= this.filteredHeight[1]);
        accordions = accordions.filter(a => a.width >= this.filteredWidth[0] && a.width <= this.filteredWidth[1]);
        if(this.mode === 'rent') this.setFilteredAccordions(accordions.filter(a => a.rental));
        else this.setFilteredAccordions(accordions)
    }

    resetFilter = () => {
        this.setFilteredBrand(undefined);
        this.setFilteredBassType(undefined);
        this.setFilteredRightSystem(undefined);
        this.setFilteredRightBtnRow(undefined);
        this.setFilteredNotesRightBtn(undefined);
        this.setFilteredNotesRightPiano(undefined);
        this.setFilteredChorusRight(undefined);
        this.setFilteredCassottoRight(undefined);
        this.setFilteredRegisterRight(undefined);
        this.setFilteredChinSwitches(undefined);
        this.setFilteredRowsBtnLeft(undefined);
        this.setFilteredStandardBass(undefined)
        this.setFilteredStandardBassChorus(undefined);
        this.setFilteredRegisterSb(undefined);
        this.setFilteredNotesFb(undefined);
        this.setFilteredRegisterFb(undefined);
        this.setFilteredChorusFb(undefined);
        this.setFilteredWeight(this.findLightiestAndHeaviestWeight());
        this.setFilteredHeight(this.findLowestAndHighestHeight());
        this.setFilteredWidth(this.findLowestAndHighestWidth());
        this.setFilteredDurallReeds(false);
        this.setFilteredTamReeds(false);
        this.setFilteredFamReeds(false);
        this.setFilteredTurboReeds(false);
        this.setFilteredUsed(false);
    }

    isFilterSet = () =>
            this.filteredBrand !== undefined || this.filteredBassType !== undefined || this.filteredRightSystem !== undefined ||
            this.filteredRightBtnRow !== undefined || this.filteredNotesRightBtn !== undefined || this.filteredChorusRight !== undefined ||
            this.filteredCassottoRight !== undefined || this.filteredRegisterRight !== undefined || this.filteredChinSwitches !== undefined ||
            this.filteredRowsBtnLeft !== undefined || this.filteredStandardBassChorus !== undefined || this.filteredStandardBass !== undefined ||
            this.filteredRegisterSb !== undefined || this.filteredNotesFb !== undefined || this.filteredRegisterFb !== undefined ||
            this.filteredChorusFb !== undefined || this.filteredNotesRightPiano !== undefined || this.filteredDurallReeds ||
            this.filteredTurboReeds || this.filteredTamReeds || this.filteredFamReeds || this.filteredUsed ||
            this.filteredHeight[0] !== this.findLowestAndHighestHeight()[0] || this.filteredHeight[1] !== this.findLowestAndHighestHeight()[1] ||
            this.filteredWeight[0] !== this.findLightiestAndHeaviestWeight()[0] || this.filteredWeight[1] !== this.findLightiestAndHeaviestWeight()[1] ||
            this.filteredWidth[0] !== this.findLowestAndHighestWidth()[0] || this.filteredWidth[1] !== this.findLowestAndHighestWidth()[1];

    findLightiestAndHeaviestWeight = (): [number, number] => {
        return getMinMax(this.accordions, 'weight')
    }
    findLowestAndHighestHeight = (): [number, number] => {
        return getMinMax(this.accordions, 'height')
    }
    findLowestAndHighestWidth = (): [number, number] => {
        return getMinMax(this.accordions, 'width');
    }
    findDistinctBtnRows = (): number[] => getDistinct(this.accordions, 'rowsButRight');
    findDistinctNotesRightBtns = (): Accordion[] => getDistinct(this.accordions, 'buttonRight', true);
    findDistinctChorusRight = (): number[] => getDistinct(this.accordions, 'chorusRight');
    findDistinctCassottoRight = (): number[] => getDistinct(this.accordions, 'casRight');
    findDistinctRegisterRight = (): string[] => this.sortRegister(getDistinct(this.accordions, 'registerRight'));
    findDistinctChinSwitches = (): number[] => getDistinct(this.accordions, 'chinSwitches');
    findDistinctRowsBtnLeft = (): number[] => getDistinct(this.accordions, 'rowsButLeft');
    findDistinctStandardBass = (): number[] => getDistinct(this.accordions, 'sbLeft');
    findDistinctStandardBassChorus = (): number[] => getDistinct(this.accordions, 'chorusSb');
    findDistinctRegisterSb = (): string[] => this.sortRegister(getDistinct(this.accordions, 'registerSbLeft'));
    findDistinctNotesFb = (): Accordion[] => getDistinct(this.accordions, 'fbLeft', true)
    findDistinctRegisterFb = (): string[] => this.sortRegister(getDistinct(this.accordions, 'registerFbLeft'))
    findDistinctChorusFb = (): string[] => getDistinct(this.accordions, 'chorusFb')
    findDistinctNotesPianoRight = (): Accordion[] => getDistinct(this.accordions, 'keysPianoRight', true);

    sortRegister = (array: string[]):string[] => {
        array = array.filter(d => d !== "null")
        return array.sort((a:string , b: string) => {
            let nrA: number;
            let nrB: number;
            if(a.includes('+')) nrA = parseInt(a.substring(0, a.indexOf('+')))
            else nrA = parseInt(a);
            if(b.includes('+')) nrB = parseInt(b.substring(0, b.indexOf('+')))
            else nrB = parseInt(b);
            if(nrA === nrB){
                if(a.length > b.length) return 1;
                else return -1;
            }
            if(nrA > nrB) return 1;
            else return -1;
        })
    }

    setIsLoading = (isLoading: boolean) => { this.isLoading = isLoading }
    setAttributeIsLoading = (attributeName: string, value: boolean) => {
        this.attributeIsLoading.set(attributeName, value)
    }
    setAccordions= (accordions: Accordion[]) => { this.accordions = accordions }

    //--------------------FILTER----------------------

    setFilteredAccordions = (accordions: Accordion[]) => { this.filteredAccordions = accordions }
    setFilteredBrand = (brand: Brand | undefined, force: boolean = false) => {
        if(force) this.filteredBrand = brand
        else if(this.filteredBrand && this.filteredBrand === brand) this.filteredBrand = undefined;
        else this.filteredBrand = brand
        this.applyFilter();
    }
    setFilteredBassType = (bassType: BassType | undefined) => {
        if(this.filteredBassType && this.filteredBassType === bassType) this.filteredBassType = undefined;
        else this.filteredBassType = bassType;
        this.applyFilter();
    }
    setFilteredRightSystem = (rightHandSystem: RightHandSystem | undefined) => {
        if(this.filteredRightSystem && this.filteredRightSystem === rightHandSystem) this.filteredRightSystem = undefined;
        else this.filteredRightSystem = rightHandSystem;
        this.applyFilter();
    }
    setFilteredWeight = (newValue: [number, number]) => {
        this.filteredWeight = newValue;
        this.applyFilter();
    }
    setFilteredHeight = (newValue: [number, number]) => {
        this.filteredHeight = newValue;
        this.applyFilter();
    }
    setFilteredWidth = (newValue: [number, number]) => {
        this.filteredWidth = newValue;
        this.applyFilter();
    }
    setFilteredRightBtnRow = (newValue: number | undefined) => {
        if(this.filteredRightBtnRow && this.filteredRightBtnRow === newValue) this.filteredRightBtnRow = undefined;
        else this.filteredRightBtnRow = newValue;
        this.applyFilter();
    }
    setFilteredNotesRightBtn = (newValue: number | undefined) => {
        if(this.filteredNotesRightBtn && this.filteredNotesRightBtn === newValue) this.filteredNotesRightBtn = undefined;
        else this.filteredNotesRightBtn = newValue;
        this.applyFilter();
    }
    setFilteredChorusRight = (newValue: number | undefined) => {
        if(this.filteredChorusRight && this.filteredChorusRight === newValue) this.filteredChorusRight = undefined;
        else this.filteredChorusRight = newValue;
        this.applyFilter();
    }
    setFilteredCassottoRight = (newValue: number | undefined) =>{
        if(this.filteredCassottoRight && this.filteredCassottoRight === newValue) this.filteredCassottoRight = undefined;
        else this.filteredCassottoRight = newValue;
        this.applyFilter();
    }
    setFilteredRegisterRight = (newValue: number | undefined) =>{
        if(this.filteredRegisterRight && this.filteredRegisterRight === newValue) this.filteredRegisterRight = undefined;
        else this.filteredRegisterRight = newValue;
        this.applyFilter();
    }
    setFilteredChinSwitches = (newValue: number | undefined) => {
        if(this.filteredChinSwitches && this.filteredChinSwitches === newValue) this.filteredChinSwitches = undefined;
        else this.filteredChinSwitches = newValue;
        this.applyFilter();
    }
    setFilteredRowsBtnLeft = (newValue: number | undefined) => {
        if(this.filteredRowsBtnLeft && this.filteredRowsBtnLeft === newValue) this.filteredRowsBtnLeft = undefined;
        else this.filteredRowsBtnLeft = newValue;
        this.applyFilter();
    }
    setFilteredStandardBass = (newValue: number | undefined) => {
        if(this.filteredStandardBass && this.filteredStandardBass === newValue) this.filteredStandardBass = undefined;
        else this.filteredStandardBass = newValue;
        this.applyFilter();
    }
    setFilteredStandardBassChorus = (newValue: number | undefined) => {
        if(this.filteredStandardBassChorus && this.filteredStandardBassChorus === newValue) this.filteredStandardBassChorus = undefined;
        else this.filteredStandardBassChorus = newValue;
        this.applyFilter();
    }
    setFilteredRegisterSb = (newValue: string | undefined) => {
        if(this.filteredRegisterSb && this.filteredRegisterSb === newValue) this.filteredRegisterSb = undefined;
        else this.filteredRegisterSb = newValue;
        this.applyFilter();
    }
    setFilteredNotesFb = (newValue: number | undefined) => {
        if(this.filteredNotesFb && this.filteredNotesFb === newValue) this.filteredNotesFb = undefined;
        else this.filteredNotesFb = newValue;
        this.applyFilter();
    }
    setFilteredRegisterFb = (newValue: string | undefined) => {
        if(this.filteredRegisterFb && this.filteredRegisterFb === newValue) this.filteredRegisterFb = undefined;
        else this.filteredRegisterFb = newValue;
        this.applyFilter();
    }
    setFilteredChorusFb = (newValue: string | undefined) => {
        if(this.filteredChorusFb && this.filteredChorusFb === newValue) this.filteredChorusFb = undefined;
        else this.filteredChorusFb = newValue;
        this.applyFilter();
    }
    setFilteredNotesRightPiano = (newValue: number | undefined) => {
        if(this.filteredNotesRightPiano && this.filteredNotesRightPiano === newValue) this.filteredNotesRightPiano = undefined;
        else this.filteredNotesRightPiano = newValue;
        this.applyFilter();
    }
    setFilteredTamReeds = (newValue: boolean) => {
        this.filteredTamReeds = newValue;
        this.applyFilter();
    }
    setFilteredFamReeds = (newValue: boolean) => {
        this.filteredFamReeds = newValue;
        this.applyFilter();
    }
    setFilteredDurallReeds = (newValue: boolean) => {
        this.filteredDurallReeds = newValue;
        this.applyFilter();
    }
    setFilteredTurboReeds = (newValue: boolean) => {
        this.filteredTurboReeds = newValue;
        this.applyFilter();
    }
    setMode = (mode: 'rent' | 'buy') => {
        this.mode = mode;
    }
    setFilteredUsed = (used: boolean) => {
        this.filteredUsed = used;
        this.applyFilter();
    }
}